/**
 * Role-based dashboard routing.
 *
 * Single source of truth for "where does this user land after login?".
 * Used by Login.tsx, OAuthCallback.tsx, and ProtectedRoute as an unauthorized
 * fallback target.
 */
export type Role = "super_admin" | "manager" | "agent" | "client";

/** What each role is called on screen. */
export const ROLE_LABEL: Record<Role, string> = {
  super_admin: "Super Admin",
  manager: "Manager",
  agent: "Agent",
  client: "Client",
};

export const DASHBOARD_PATH_BY_ROLE: Record<Role, string> = {
  // super_admin lands on its own dashboard (admin-quick-actions grid).
  // The operational view (approvals + team capacity) is still reachable
  // via the "Operational dashboard" card on /admin/dashboard. Splitting
  // the landing pages stops super_admin from inheriting the "manager"
  // framing across the entire chrome.
  super_admin: "/admin/dashboard",
  manager: "/manager/dashboard",
  agent: "/agent/dashboard",
  client: "/client/dashboard",
};

export function dashboardPathForRole(role: string | null | undefined): string {
  if (!role) return "/login";
  return DASHBOARD_PATH_BY_ROLE[role as Role] ?? "/login";
}

/**
 * Where a person goes right after signing in, however they signed in: a forced
 * password change comes first, then a forced second-factor enrolment, and only
 * then their role's dashboard.
 */
export function nextPathForToken(token: {
  role: string;
  must_change_password?: boolean;
  must_enroll_mfa?: boolean;
}): string {
  if (token.must_change_password) return "/auth/change-password";
  if (token.must_enroll_mfa) return "/auth/mfa-enroll";
  return dashboardPathForRole(token.role);
}
